"use client"

import '@/lib/pwa/captureInstallPrompt'
import { AntdRegistry } from '@ant-design/nextjs-registry';

import StoreProvider, { storeRef } from "@/components/providers/StoreProvider";

import ThemeProvider from "@/components/providers/ThemeProvider";

import PageProvider from "@/components/providers/PageProvider";

import ToastProvider from "@/components/providers/ToastProvider";
import PwaProvider from "@/components/providers/PwaProvider";
import TrackingProvider from "@/components/providers/TrackingProvider";
import { Suspense, useEffect, useRef } from "react";
import PageLoading from "@/components/common/PageLoading";
import GlobalActions from "@/components/common/FloatButton/GlobalActions";


import { APIAuthen } from '@/lib/redux/features/user/type';
import dayjs from 'dayjs';
import { I18nextProvider } from 'react-i18next';
import { thunks } from '@/lib/redux/thunks';
import i18n from '@/lib/i18n';
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export default function AppLayoutWrapper({
  children,
  apiAuthen,
  lang,
  isDesktop
}: Readonly<{
  children: React.ReactNode;
  apiAuthen: APIAuthen,
  lang: any,
  isDesktop: boolean
}>) {

  const initial = useRef<boolean>(false)

  if (!initial.current) {
    // Use server `lang` prop (not pathname / LanguageDetector) so SSR text matches CSR.
    const resolvedLang = lang === "en" ? "en" : "vi"
    dayjs.locale(resolvedLang)
    if (i18n.language !== resolvedLang) {
      // Sync so t() in this render tree sees the same language as SSR.
      i18n.language = resolvedLang
    }

    initial.current = true
  }

  useEffect(() => {
    const resolvedLang = lang === "en" ? "en" : "vi"
    dayjs.locale(resolvedLang)
    if (i18n.language !== resolvedLang) {
      void i18n.changeLanguage(resolvedLang)
    }
    if (resolvedLang === "en") {
      storeRef?.current?.dispatch(thunks.get_exchange_currency({})).unwrap().catch(() => { })
    }
  }, [lang])

  useEffect(() => {
    const removeChatwoot = () => {
      document
        .querySelectorAll(".woot-widget-bubble, .woot-widget-holder, .woot-elements--right, .woot--bubble-holder, #woot-widget")
        .forEach((el) => el.remove())
      document
        .querySelectorAll('script[src*="chatwoot"], script#chatwoot-sdk')
        .forEach((el) => el.remove())
      const chatwoot = (window as Window & { $chatwoot?: { toggleBubbleVisibility?: (v: string) => void } }).$chatwoot
      chatwoot?.toggleBubbleVisibility?.("hide")
    }

    removeChatwoot()
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          if (
            node.matches(".woot-widget-bubble, .woot-widget-holder, .woot-elements--right, .woot--bubble-holder, #woot-widget") ||
            node.querySelector(".woot-widget-bubble, .woot-widget-holder, .woot-elements--right, .woot--bubble-holder, #woot-widget")
          ) {
            removeChatwoot()
            return
          }
        }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!apiAuthen.accessToken) return;
    localStorage.setItem('apiAccessToken', apiAuthen.accessToken)
  }, [apiAuthen])

  return (
    // AntdRegistry must wrap ConfigProvider so SSR style extraction + prefixCls stay in sync.
    <AntdRegistry layer hashPriority='low'>
      <ThemeProvider lang={lang}>
        <StoreProvider isDesktop={isDesktop}>
          <PageProvider isDesktop={isDesktop}>
            <ToastProvider />
            <TrackingProvider />
            <I18nextProvider i18n={i18n}>
              <PwaProvider isDesktop={isDesktop}>
                <GlobalActions />
                <Suspense fallback={<PageLoading />}>
                  {children}
                </Suspense>
              </PwaProvider>
            </I18nextProvider>
          </PageProvider>
        </StoreProvider>
      </ThemeProvider>
    </AntdRegistry>
  );
}
